import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  PoundSterling, TrendingUp, Users, CreditCard, GraduationCap,
  RefreshCw, Clock, CheckCircle, AlertTriangle, ArrowUpRight, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import StudentFeesExport from './StudentFeesExport';

const FinanceTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/payments/finance-summary');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-white/30 mb-3" />
        <p className="text-white/40">Loading finance data...</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="finance-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-400" />
          Finance Overview
        </h2>
        <Button variant="outline" onClick={fetchData} className="border-[#333] text-white hover:bg-[#222]" data-testid="refresh-finance">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Student fees export */}
      <StudentFeesExport />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#111] border-[#222]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <PoundSterling className="w-5 h-5 text-emerald-400" />
              <Badge className="bg-emerald-500/15 text-emerald-400 text-[10px] border border-emerald-500/30">collected</Badge>
            </div>
            <p className="text-3xl font-bold text-white" data-testid="total-revenue">£{data.total_revenue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            <p className="text-white/40 text-xs mt-1">Total revenue</p>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#222]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-amber-400" />
              <Badge className="bg-amber-500/15 text-amber-400 text-[10px] border border-amber-500/30">pending</Badge>
            </div>
            <p className="text-3xl font-bold text-white" data-testid="total-pending">£{data.total_pending.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            <p className="text-white/40 text-xs mt-1">{data.total_awaiting} awaiting payment</p>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#222]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-cyan-400" />
              <Badge className="bg-cyan-500/15 text-cyan-400 text-[10px] border border-cyan-500/30">families</Badge>
            </div>
            <p className="text-3xl font-bold text-white">{data.unique_paying_families}</p>
            <p className="text-white/40 text-xs mt-1">Paying families</p>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#222]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <ArrowUpRight className="w-5 h-5 text-indigo-400" />
              <Badge className="bg-indigo-500/15 text-indigo-400 text-[10px] border border-indigo-500/30">monthly</Badge>
            </div>
            <p className="text-3xl font-bold text-white">£{data.monthly_recurring.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            <p className="text-white/40 text-xs mt-1">{data.total_active_subscriptions} active subscription{data.total_active_subscriptions !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Class */}
        <Card className="bg-[#111] border-[#222]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <PoundSterling className="w-4 h-4 text-emerald-400" />
              Revenue by Class
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.revenue_by_class.map((cls, i) => {
                const maxRevenue = data.revenue_by_class[0]?.revenue || 1;
                const pct = (cls.revenue / maxRevenue) * 100;
                return (
                  <div key={i} className="group">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-white/80 truncate mr-3">{cls.class_name}</span>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-white/40 text-xs">{cls.students} student{cls.students !== 1 ? 's' : ''}</span>
                        <span className="text-emerald-400 font-medium">£{cls.revenue.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-[#222] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="bg-[#111] border-[#222]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              Recent Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_payments.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#222] last:border-0">
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{p.student_name}</p>
                    <p className="text-white/40 text-xs truncate">{p.class_name}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {p.is_scholarship ? (
                      <Badge className="bg-purple-500/15 text-purple-400 text-[10px] border border-purple-500/30">
                        <GraduationCap className="w-3 h-3 mr-1" />scholarship
                      </Badge>
                    ) : (
                      <span className="text-emerald-400 text-sm font-medium">£{p.amount.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Active Subscriptions */}
        {data.active_subscriptions.length > 0 && (
          <Card className="bg-[#111] border-[#222]">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-indigo-400" />
                Active Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.active_subscriptions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#222] last:border-0">
                    <div>
                      <p className="text-white text-sm">{s.student_name}</p>
                      <p className="text-white/40 text-xs">{(s.class_names || []).join(', ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-indigo-400 text-sm font-medium">£{s.monthly_amount.toFixed(2)}/mo</p>
                      {s.next_payment_date && (
                        <p className="text-white/30 text-[10px]">Next: {new Date(s.next_payment_date).toLocaleDateString('en-GB')}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Outstanding Payments */}
        {data.outstanding.length > 0 && (
          <Card className="bg-[#111] border-[#222]">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                Outstanding Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.outstanding.map((o, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#222] last:border-0">
                    <div>
                      <p className="text-white text-sm">{o.student_name}</p>
                      <p className="text-white/40 text-xs">{o.class_name}</p>
                    </div>
                    <span className="text-amber-400 text-sm font-medium">£{o.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Types Breakdown */}
        <Card className="bg-[#111] border-[#222]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-cyan-400" />
              Payment Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.payment_types.map((pt, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-white/5 text-white/60 text-xs border border-white/10 capitalize">{pt.type.replace(/_/g, ' ')}</Badge>
                    <span className="text-white/40 text-xs">{pt.count} payment{pt.count !== 1 ? 's' : ''}</span>
                  </div>
                  <span className="text-white font-medium">£{pt.revenue.toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-[#333] pt-3 flex items-center justify-between">
                <span className="text-white/60 text-sm">Total collected</span>
                <span className="text-emerald-400 font-bold text-lg">£{data.total_revenue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
              </div>
              {data.total_scholarships > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <GraduationCap className="w-4 h-4 text-purple-400" />
                  <span className="text-white/40 text-sm">{data.total_scholarships} scholarship{data.total_scholarships !== 1 ? 's' : ''} awarded</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinanceTab;
